import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type {
  BulkDeleteMarkersResponse,
  BulkMoveMarkersResponse,
  UseGridBulkActions,
} from "./types";

export const useGridBulkActions: UseGridBulkActions = ({
  floorPlanId,
  selectedElementIds,
  onBulkDeleteElements,
  onBulkMoveElements,
  onActionSucceeded,
}) => {
  const { t } = useTranslation();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [moveTargetGroupLabel, setMoveTargetGroupLabel] = useState("");
  const [isMoveConfirmOpen, setIsMoveConfirmOpen] = useState(false);

  const { mutateAsync: runBulkDelete, isPending: isDeletePending } =
    useMutation({
      mutationFn: (markerIds: number[]): Promise<BulkDeleteMarkersResponse> =>
        api.bulkDeleteQuantitySurveyorElementMarkers({
          floorPlanId,
          markerIds,
        }),
      onSuccess: (response) => {
        const deletedElementIds = response.deletedMarkerIds.map(String);
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ELEMENTS_BULK_DELETED, {
          elementCount: deletedElementIds.length,
        });
        onBulkDeleteElements({ floorPlanId, elementIds: deletedElementIds });
        onActionSucceeded(deletedElementIds);
      },
      onError: () => {
        toast.error(t("smartSurveyor.gridReview.deleteFailed"));
      },
    });

  const { mutateAsync: runBulkMove, isPending: isMovePending } = useMutation({
    mutationFn: ({
      markerIds,
      elementTypeName,
    }: {
      markerIds: number[];
      elementTypeName: string;
    }): Promise<BulkMoveMarkersResponse> =>
      api.bulkMoveQuantitySurveyorElementMarkers({
        floorPlanId,
        markerIds,
        elementTypeName,
      }),
    onSuccess: (response) => {
      const movedElementIds = response.movedMarkerIds.map(String);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ELEMENTS_BULK_MOVED, {
        elementCount: movedElementIds.length,
      });
      onBulkMoveElements({
        floorPlanId,
        elementIds: movedElementIds,
        toGroupLabel: response.elementTypeName,
        newGroupElementTypeId: response.elementTypeId,
      });
      onActionSucceeded(movedElementIds);
    },
    onError: () => {
      toast.error(t("smartSurveyor.gridReview.moveFailed"));
    },
  });

  const openDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(false);
  }, []);

  const deleteSelectedElements = useCallback(async () => {
    if (selectedElementIds.size === 0) {
      return;
    }
    await runBulkDelete([...selectedElementIds].map(Number));
  }, [selectedElementIds, runBulkDelete]);

  const openMoveConfirm = useCallback(() => {
    // Normalize the typed name once, at the commit boundary.
    setMoveTargetGroupLabel((currentLabel) => currentLabel.trim());
    setIsMoveConfirmOpen(true);
  }, []);

  const closeMoveConfirm = useCallback(() => {
    setIsMoveConfirmOpen(false);
  }, []);

  const moveSelectedElements = useCallback(async () => {
    if (selectedElementIds.size === 0 || moveTargetGroupLabel === "") {
      return;
    }
    await runBulkMove({
      markerIds: [...selectedElementIds].map(Number),
      elementTypeName: moveTargetGroupLabel,
    });
  }, [selectedElementIds, moveTargetGroupLabel, runBulkMove]);

  return {
    isBulkActionRunning: isDeletePending || isMovePending,
    isDeleteConfirmOpen,
    openDeleteConfirm,
    closeDeleteConfirm,
    deleteSelectedElements,
    moveTargetGroupLabel,
    setMoveTargetGroupLabel,
    isMoveConfirmOpen,
    openMoveConfirm,
    closeMoveConfirm,
    moveSelectedElements,
  };
};
